/**
 * @module builder
 */
import type { TieredPackageFeature, TieredPackageTier, TieredPackageTierId } from 'paintscout'
import { uuid } from '../../util'
import { TIER_ORDER } from './default-tiered-packages'

/**
 * The feature list belongs to the package set: one row per feature, with each tier marking it
 * included or excluded. Older package sets stored a separate `string[]` on every tier, so
 * everything reads through getTieredPackageFeatures, which folds those legacy lists into rows.
 */

export type TieredPackageFeatureSource = {
  features?: TieredPackageFeature[]
  tiers: Record<TieredPackageTierId, TieredPackageTier>
}

const allIncluded = (): Record<TieredPackageTierId, boolean> =>
  TIER_ORDER.reduce((acc, tierId) => ({ ...acc, [tierId]: true }), {} as Record<TieredPackageTierId, boolean>)

/** A blank row, included everywhere — what "Add Feature" creates. */
export function createTieredPackageFeature(label = ''): TieredPackageFeature {
  return { id: uuid(), label, included: allIncluded() }
}

/**
 * Fold the legacy per-tier `features: string[]` arrays into shared rows. Rows are keyed by
 * label so a feature repeated across tiers becomes one row included in each of them, which
 * is how these lists were authored in practice.
 */
function migrateLegacyFeatures(tiers: Record<TieredPackageTierId, TieredPackageTier>): TieredPackageFeature[] {
  const rows: TieredPackageFeature[] = []
  const byLabel = new Map<string, TieredPackageFeature>()

  TIER_ORDER.forEach((tierId) => {
    ;(tiers[tierId]?.features ?? []).forEach((label) => {
      const key = label.trim().toLowerCase()
      const existing = key ? byLabel.get(key) : undefined
      if (existing) {
        existing.included[tierId] = true
        return
      }
      const row: TieredPackageFeature = {
        id: uuid(),
        label,
        included: TIER_ORDER.reduce(
          (acc, id) => ({ ...acc, [id]: id === tierId }),
          {} as Record<TieredPackageTierId, boolean>
        )
      }
      rows.push(row)
      if (key) {
        byLabel.set(key, row)
      }
    })
  })

  return rows
}

/** The shared feature rows for a package set, migrating legacy per-tier lists when needed. */
export function getTieredPackageFeatures(source: TieredPackageFeatureSource): TieredPackageFeature[] {
  if (source.features) {
    return source.features
  }
  return migrateLegacyFeatures(source.tiers)
}

/** The rows as one tier sees them — label plus whether that tier includes it. */
export function getTierFeatures(
  source: TieredPackageFeatureSource,
  tierId: TieredPackageTierId
): Array<{ id: string; label: string; included: boolean }> {
  return getTieredPackageFeatures(source).map((feature) => ({
    id: feature.id,
    label: feature.label,
    included: feature.included?.[tierId] !== false
  }))
}

/** Just the labels a tier includes — the shape the customer-facing card renders. */
export function getIncludedFeatureLabels(source: TieredPackageFeatureSource, tierId: TieredPackageTierId): string[] {
  return getTierFeatures(source, tierId)
    .filter((feature) => feature.included && feature.label.trim())
    .map((feature) => feature.label)
}

/**
 * Package descriptions are plain text, but the quote-level setup wizard used to author them
 * with the rich-text editor, so saved values can still be TipTap HTML. Rendering those as text
 * would show raw markup to the customer, and rendering them as HTML is an injection sink — so
 * flatten to text: block ends become newlines, tags go, entities decode.
 */
export function getTieredPackageDescriptionText(description?: string): string {
  if (!description) {
    return ''
  }
  if (!/<[a-z/][\s\S]*>/i.test(description)) {
    return description
  }
  return description
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Move a row to sit where another row currently is (drag reorder). */
export function reorderTieredPackageFeatures(
  features: TieredPackageFeature[],
  draggedId: string,
  targetId: string
): TieredPackageFeature[] {
  if (draggedId === targetId) {
    return features
  }
  const next = [...features]
  const from = next.findIndex((f) => f.id === draggedId)
  const to = next.findIndex((f) => f.id === targetId)
  if (from < 0 || to < 0) {
    return features
  }
  const [row] = next.splice(from, 1)
  next.splice(to, 0, row)
  return next
}
